import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { Newspaper, Zap, Star, BarChart3, MessageSquare, ArrowRight, TrendingUp, Bitcoin, Gem, LineChart } from 'lucide-react';
import { siteConfig } from '@/config/site';

const categories = [
    {
        icon: TrendingUp,
        title: 'Forex',
        description: 'Major and minor currency pair analysis, central bank decisions, and forex market outlook.',
    },
    {
        icon: Bitcoin,
        title: 'Crypto',
        description: 'Cryptocurrency market updates, blockchain developments, and digital asset trends.',
    },
    {
        icon: Gem,
        title: 'Commodities',
        description: 'Gold, oil, and commodity market news with supply-demand analysis and price forecasts.',
    },
    {
        icon: LineChart,
        title: 'Stocks',
        description: 'Equity market coverage, earnings reports, and stock index movements worldwide.',
    },
];

const features = [
    {
        icon: Zap,
        title: 'Real-Time',
        description: 'Breaking news delivered as it happens with minimal delay from source to screen.',
    },
    {
        icon: Star,
        title: 'Curated',
        description: 'Carefully selected stories relevant to traders, filtered from the noise.',
    },
    {
        icon: BarChart3,
        title: 'Analysis',
        description: 'In-depth market analysis accompanying every major news event.',
    },
    {
        icon: MessageSquare,
        title: 'Expert Commentary',
        description: 'Professional insights and commentary from experienced market analysts.',
    },
];

export default function News() {
    return (
        <AppLayout title={`Market News - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <FadeIn>
                    <div className="text-center max-w-4xl mx-auto">
                        <Badge className="mb-6">Trading Tools</Badge>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            Market <span className="text-gradient">News</span>
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                            Stay informed with the latest market news, expert analysis, and real-time
                            updates across all major financial markets.
                        </p>
                    </div>
                </FadeIn>
            </Section>

            {/* News Categories */}
            <Section className="py-20 bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Latest News"
                        highlight="Categories"
                        description="Browse news across all major financial markets and asset classes."
                    />
                </FadeIn>
                <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {categories.map((category) => (
                        <motion.div key={category.title} variants={staggerItem}>
                            <Card className="p-6 rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors h-full">
                                <CardContent className="p-0">
                                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 border border-primary/10 mb-4">
                                        <category.icon className="w-6 h-6 text-primary" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-foreground mb-2">
                                        {category.title}
                                    </h3>
                                    <p className="text-sm text-muted-foreground">{category.description}</p>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </StaggerChildren>
            </Section>

            {/* Placeholder */}
            <Section className="py-20 bg-background">
                <FadeIn>
                    <Card className="max-w-4xl mx-auto rounded-xl border border-border bg-card/80 backdrop-blur-sm">
                        <CardContent className="p-12 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-primary/10 border border-primary/10 mb-6">
                                <Newspaper className="w-8 h-8 text-primary" />
                            </div>
                            <h2 className="text-2xl font-bold text-foreground mb-4">
                                News Feed Coming Soon
                            </h2>
                            <p className="text-muted-foreground max-w-xl mx-auto">
                                Our curated news feed will aggregate the most relevant financial news
                                from trusted sources, filtered and organized for active traders. Check
                                back soon for real-time market coverage.
                            </p>
                        </CardContent>
                    </Card>
                </FadeIn>
            </Section>

            {/* Features */}
            <Section className="py-20 bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Why Our"
                        highlight="News Feed"
                        description="Built specifically for traders who need fast, reliable market intelligence."
                    />
                </FadeIn>
                <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {features.map((feature) => (
                        <motion.div key={feature.title} variants={staggerItem}>
                            <Card className="text-center p-6 rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors h-full">
                                <CardContent className="p-0">
                                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 border border-primary/10 mb-4">
                                        <feature.icon className="w-6 h-6 text-primary" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-foreground mb-2">
                                        {feature.title}
                                    </h3>
                                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </StaggerChildren>
            </Section>

            {/* CTA */}
            <Section className="py-20 bg-background">
                <FadeIn>
                    <div className="text-center max-w-2xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            Trade With <span className="text-gradient">Confidence</span>
                        </h2>
                        <p className="text-muted-foreground mb-8">
                            Join {siteConfig.brand.name} and never miss a market-moving headline again.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    Get Started <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </a>
                            <Link to="/tools/news">
                                <Button variant="outline" size="lg">Explore Tools</Button>
                            </Link>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
